import { useEffect, useRef, type ReactNode } from "react";
import { ActionPair, CheckIco, actionPairCss as ap } from "./ActionPair";
import { hasOpenSheet } from "./SheetDialog";
import css from "./ConfirmModal.module.css";

/**
 * ConfirmModal — кастомная confirm-модалка (A3: никаких нативных confirm/alert).
 * 03.10 (владелец: увольнение в HR): переехала сюда из screens/eo/EoConfigModal.tsx
 * (CfgModal — реэкспорт там же, поведение прежнее). Эталон screen-17 v63: срез углов
 * clip-path 10px, тона danger (удаление) и warn (несохранённая правка); футер —
 * ActionPair: confirm СЛЕВА (danger — красная без иконки, warn — зелёная с «✓»),
 * «Отмена» справа. Рендерится инлайн с fixed-оверлеем на var(--z-modal) — ниже шитов
 * TimePicker/NumPad/TextPad и Dropdown.
 *
 * Esc → onCancel (планка кодовой базы — CustomTimeModal), слушатель в capture-фазе со
 * stopPropagation; 03.10: пока поверх открыт шит (TextPad причины) или поповер (Dropdown
 * причины, PhonePad — портал в конец body, ПОСЛЕ нашего хоста) — Esc его, не наш (ревью:
 * раньше Esc в списке причин закрывал всю модалку увольнения).
 * Фокус уводится в панель, при закрытии возвращается на opener.
 *
 * data-cfg-modal — маркер для outside-click-гарда inline-редактора чайной.
 *
 * Аддитивно (03.10): actionDisabled/cancelDisabled — пока идёт запрос; cancelLabel;
 * solo — одна кнопка действия (экран итога «Понятно»); тап по подложке, которым
 * закрывают Dropdown/пад поверх модалки, модалку не закрывает.
 */
export interface ConfirmModalProps {
  tone: "danger" | "warn";
  title: string;
  actionLabel: string;
  /** Доп. класс confirm-кнопки — screen-21 guard-модалка расширяет её. */
  actionClassName?: string;
  actionDisabled?: boolean;
  cancelLabel?: string;
  cancelDisabled?: boolean;
  /** Только кнопка действия, без «Отмены» (итоговое сообщение). */
  solo?: boolean;
  onAction: () => void;
  onCancel: () => void;
  children: ReactNode;
}

/** Поверх модалки открыт шит или поповер: listbox/dialog, который в DOM идёт ПОСЛЕ хоста и не внутри
 *  него (модалка-подложка, внутри которой мы сами, — предок или раньше по документу, её не считаем). */
function overlayAbove(host: HTMLElement | null): boolean {
  if (hasOpenSheet()) return true;
  if (!host) return false;
  return [...document.querySelectorAll('[role="listbox"], [role="dialog"]')]
    .some((el) => !host.contains(el) && !!(host.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
}

export function ConfirmModal(p: ConfirmModalProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef(p.onCancel);
  cancelRef.current = p.cancelDisabled ? () => {} : p.onCancel;

  /* 03.10: тап мимо открытого поверх Dropdown/пада закрывает только его, не модалку.
     Наш capture-слушатель зарегистрирован раньше слушателя поповера — видит его ещё в DOM. */
  const armed = useRef(true);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || overlayAbove(hostRef.current)) return;
      e.stopPropagation();
      cancelRef.current();
    };
    const onDown = () => { armed.current = !overlayAbove(hostRef.current); };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  const warn = p.tone === "warn";
  return (
    <div ref={hostRef} className={css.host} data-cfg-modal="">
      <div className={css.backdrop} onClick={() => { if (armed.current) cancelRef.current(); }} />
      <div ref={panelRef} tabIndex={-1} className={`${css.modal} ${warn ? css.warn : ""}`}
        role="alertdialog" aria-modal="true" aria-label={p.title}>
        <div className={css.title}>{p.title}</div>
        <div className={css.body}>{p.children}</div>
        {p.solo ? (
          <div className={ap.apair}>
            <button type="button" className={`${ap.abtn} ${warn ? ap.cf : ap.dg}`} disabled={p.actionDisabled} onClick={p.onAction}>
              {warn ? <CheckIco /> : null}
              {p.actionLabel}
            </button>
          </div>
        ) : (
          <ActionPair tone={warn ? "confirm" : "danger"} okLabel={p.actionLabel} onOk={p.onAction} okDisabled={p.actionDisabled}
            okClassName={p.actionClassName} onCancel={p.onCancel} cancelLabel={p.cancelLabel} cancelDisabled={p.cancelDisabled} />
        )}
      </div>
    </div>
  );
}
